/**
 * BundleBuilder -- PDP UI for adding packs/bundles to the cart.
 *
 * Fixed packs: stacked per-slot color/size selectors (Shapermint-style).
 * Configurable packs: pick eligible items, then configure each slot.
 */

'use client';

import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { PackEntity, PackItemEntity, ResolvedPackPriceEntity } from '@/domain/entities/pack.entity';
import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import { formatMoney } from '@/utils/formatMoney';
import {
  buildConfigurablePackSlots,
  buildFixedPackSlots,
  pickDefaultVariantId,
  type PackSlot,
} from '@/lib/pack/build-pack-slots';
import { PackItemVariantSelector } from './PackItemVariantSelector';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} 0;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subheading = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
`;

const ConfigurableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const ConfigurableRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.xs} 0;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
`;

const SelectionCounter = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.space.sm};
  flex-wrap: wrap;
`;

const BundlePrice = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.heavy};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CompareAt = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-decoration: line-through;
`;

const SavingsBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: ${({ theme }) => theme.colors.semantic.positive.color};
  color: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const AddButton = styled.button<{ disabled?: boolean }>`
  padding: 14px 24px;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: ${({ disabled, theme }) =>
    disabled ? theme.colors.shades[400].color : theme.colors.secondary};
  color: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: 0;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.shades[550]?.color || '#3a3a3a'};
  }
`;

export interface PackSelection {
  packItemId?: string;
  productId: string;
  variantId: string;
}

export interface BundleBuilderProps {
  pack: PackEntity;
  price: ResolvedPackPriceEntity | null;
  isAdding?: boolean;
  onAddToCart: (selections: PackSelection[]) => Promise<void>;
  onFirstSlotVariantChange?: (variantId: string | undefined) => void;
}

function seedVariantSelections(slots: PackSlot[]): Record<string, string> {
  const seed: Record<string, string> = {};
  for (const slot of slots) {
    const variantId = pickDefaultVariantId(slot, slot.product.variants);
    if (variantId) {
      seed[slot.slotKey] = variantId;
    }
  }
  return seed;
}

function initialConfigurableSelection(pack: PackEntity): Record<string, boolean> {
  const min = pack.min_items ?? pack.items.length;
  const seed: Record<string, boolean> = {};
  pack.items.forEach((item, index) => {
    seed[item.id] = index < min;
  });
  return seed;
}

export function BundleBuilder({
  pack,
  price,
  isAdding,
  onAddToCart,
  onFirstSlotVariantChange,
}: BundleBuilderProps) {
  const { t } = useTranslation('pdp');
  const { countryConfig } = useIntl();
  const currencyCode = countryConfig.currency.code;
  const currencySymbol = countryConfig.currency.format;
  const locale = countryConfig.locale;

  const [configurableSelected, setConfigurableSelected] = useState<Record<string, boolean>>(() =>
    pack.type === 'configurable' ? initialConfigurableSelection(pack) : {},
  );

  const activeSlots = useMemo(() => {
    if (pack.type === 'fixed') {
      return buildFixedPackSlots(pack);
    }
    const selectedIds = Object.entries(configurableSelected)
      .filter(([, on]) => on)
      .map(([id]) => id);
    return buildConfigurablePackSlots(pack, selectedIds);
  }, [pack, configurableSelected]);

  const [variantBySlot, setVariantBySlot] = useState<Record<string, string>>(() =>
    seedVariantSelections(activeSlots),
  );

  const activeSlotKeys = activeSlots.map((s) => s.slotKey).join('|');

  useEffect(() => {
    setVariantBySlot(seedVariantSelections(activeSlots));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseed when slot set changes
  }, [pack.id, activeSlotKeys]);

  useEffect(() => {
    const firstKey = activeSlots[0]?.slotKey;
    const firstVariant = firstKey ? variantBySlot[firstKey] : undefined;
    onFirstSlotVariantChange?.(firstVariant);
  }, [activeSlots, variantBySlot, onFirstSlotVariantChange]);

  const min = pack.min_items ?? pack.items.length;
  const max = pack.max_items ?? pack.items.length;
  const selectedConfigurableCount = Object.values(configurableSelected).filter(Boolean).length;
  const inRange =
    pack.type === 'fixed'
      ? activeSlots.length > 0 && activeSlots.every((s) => variantBySlot[s.slotKey])
      : selectedConfigurableCount >= min &&
        selectedConfigurableCount <= max &&
        activeSlots.every((s) => variantBySlot[s.slotKey]);

  const toggleConfigurable = (packItem: PackItemEntity) => {
    setConfigurableSelected((prev) => {
      const next = { ...prev, [packItem.id]: !prev[packItem.id] };
      const count = Object.values(next).filter(Boolean).length;
      if (!next[packItem.id] || count <= max) {
        return next;
      }
      return prev;
    });
  };

  const setSlotVariant = (slotKey: string, variantId: string) => {
    setVariantBySlot((prev) => ({ ...prev, [slotKey]: variantId }));
  };

  const handleAdd = async () => {
    if (!inRange || isAdding) return;
    const payload: PackSelection[] = [];
    for (const slot of activeSlots) {
      const variantId = variantBySlot[slot.slotKey];
      if (!variantId) continue;
      payload.push({
        packItemId: slot.packItemId,
        productId: slot.productId,
        variantId,
      });
    }
    await onAddToCart(payload);
  };

  const addDisabled = isAdding || !inRange || !price;

  return (
    <Container>
      <Heading>{t('bundle.heading', 'Bundle Contents')}</Heading>
      {pack.type === 'configurable' && (
        <>
          <Subheading>
            {t('bundle.selectItems', 'Select {{count}} items', { count: max })}
          </Subheading>
          <ConfigurableList>
            {pack.items.map((packItem) => {
              const product = packItem.product;
              const checked = !!configurableSelected[packItem.id];
              const atMax =
                !checked && Object.values(configurableSelected).filter(Boolean).length >= max;
              return (
                <ConfigurableRow key={packItem.id}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={atMax}
                    onChange={() => toggleConfigurable(packItem)}
                  />
                  <span>{product?.title ?? `Product ${packItem.product_id}`}</span>
                </ConfigurableRow>
              );
            })}
          </ConfigurableList>
          <SelectionCounter>
            {t('bundle.itemsSelected', '{{count}} of {{max}} selected', {
              count: selectedConfigurableCount,
              max,
            })}
          </SelectionCounter>
        </>
      )}

      {activeSlots.map((slot) => (
        <PackItemVariantSelector
          key={slot.slotKey}
          product={slot.product}
          selectedVariantId={variantBySlot[slot.slotKey]}
          onVariantChange={(variantId) => setSlotVariant(slot.slotKey, variantId)}
          slotLabel={slot.product.title}
        />
      ))}

      {price && (
        <PriceRow>
          <BundlePrice>
            {formatMoney(price.price.display, currencyCode, locale, currencySymbol)}
          </BundlePrice>
          {price.compare_at_price && price.compare_at_price.display > price.price.display && (
            <>
              <CompareAt>
                {formatMoney(
                  price.compare_at_price.display,
                  currencyCode,
                  locale,
                  currencySymbol,
                )}
              </CompareAt>
              <SavingsBadge>
                {t('bundle.youSave', 'You save {{amount}}', {
                  amount: formatMoney(
                    price.compare_at_price.display - price.price.display,
                    currencyCode,
                    locale,
                    currencySymbol,
                  ),
                })}
              </SavingsBadge>
            </>
          )}
        </PriceRow>
      )}

      <AddButton type="button" onClick={handleAdd} disabled={addDisabled}>
        {isAdding
          ? t('product.adding', 'Adding...')
          : !price
            ? t('bundle.unavailable', 'Unavailable')
            : t('bundle.addToCart', 'Add Pack to Cart')}
      </AddButton>
    </Container>
  );
}
